import { DownloadLink } from "./DownloadLink";
import { animate, motion, useMotionValue, useScroll, useTransform } from "framer-motion";
import { FileText, Mail, MessageCircle, MessageSquare, Phone } from "lucide-react";
import { FormEvent, type ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lang, t } from "../i18n";
import { downloadPath, leadFormPath } from "../routes";
import { reportLeadEntryConversion } from "../tracking";

function getTranslatePercent(index: number, total: number) {
  return (index / (total + 1)) * 10000;
}

const inputItems = [
  { key: "hero.demo.phone", icon: Phone },
  { key: "hero.demo.line", icon: MessageCircle },
  { key: "hero.demo.whatsapp", icon: MessageCircle },
  { key: "hero.demo.emailInput", icon: Mail },
  { key: "hero.demo.forms", icon: FileText },
  { key: "hero.demo.sms", icon: MessageSquare },
];

function SubcopyEmphasis({ children }: { children: ReactNode }) {
  return (
    <span
      className="font-extrabold"
      style={{
        backgroundImage: "linear-gradient(90deg, #5223b1 0%, #7823b1 45%, #e930c9 100%)",
        backgroundClip: "text",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "transparent",
      }}
    >
      {children}
    </span>
  );
}

function HeroSubcopy({ lang }: { lang: Lang }) {
  return (
    <>
      <SubcopyEmphasis>{t(lang, "hero.sub.role")}</SubcopyEmphasis>
      {t(lang, "hero.sub.description")}
    </>
  );
}

export function OriginalHomeHero({ lang }: { lang: Lang }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [wordIndex, setWordIndex] = useState(0);
  const [demoEmail, setDemoEmail] = useState("");
  const rotateWords = [
    t(lang, "hero.rotate.1"),
    t(lang, "hero.rotate.2"),
    t(lang, "hero.rotate.3"),
    t(lang, "hero.rotate.4"),
  ];
  const translatePercent = useMotionValue(getTranslatePercent(0, rotateWords.length));
  const translateY = useTransform(translatePercent, (value) => `${-(value / 100)}%`);
  const { scrollY } = useScroll();
  const ctaOpacity = useTransform(scrollY, [240, 420], [1, 0.22]);
  const demoOpacity = useTransform(scrollY, [360, 520], [1, 0.12]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextIndex = wordIndex + 1;
      const isLastWord = wordIndex === rotateWords.length - 1;

      if (isLastWord) {
        setWordIndex(0);
      }

      animate(translatePercent, getTranslatePercent(nextIndex, rotateWords.length), {
        duration: 0.72,
        ease: [0.22, 1, 0.36, 1],
      }).then(() => {
        if (isLastWord) {
          translatePercent.set(0);
        }
      });

      if (!isLastWord) {
        setWordIndex(nextIndex);
      }
    }, 1600);

    return () => window.clearTimeout(timer);
  }, [wordIndex, rotateWords.length, translatePercent]);

  useEffect(() => {
    setWordIndex(0);
    translatePercent.set(0);
  }, [lang, translatePercent]);

  function submitDemoEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    reportLeadEntryConversion(search);
    navigate(leadFormPath(lang, demoEmail, "hero_demo_form", search));
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-white to-[#f8f9fa] px-5 pt-44 sm:px-10 md:pt-48">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[430px] overflow-hidden">
        <div
          className="absolute left-0 top-0 h-[430px] w-[72%] opacity-90"
          style={{
            background:
              "linear-gradient(118deg, rgba(82,35,177,.42) 0%, rgba(120,35,177,.26) 16%, rgba(255,255,255,0) 17%), linear-gradient(126deg, rgba(233,48,201,.26) 0%, rgba(233,48,201,.16) 24%, rgba(255,255,255,0) 25%), linear-gradient(138deg, rgba(82,35,177,.22) 0%, rgba(82,35,177,.10) 34%, rgba(255,255,255,0) 35%), radial-gradient(circle at 0% 0%, rgba(233,48,201,.22) 0%, rgba(255,255,255,0) 52%)",
          }}
        />
        <div
          className="absolute right-0 top-0 h-[430px] w-[72%] opacity-80"
          style={{
            background:
              "linear-gradient(242deg, rgba(82,35,177,.38) 0%, rgba(120,35,177,.22) 16%, rgba(255,255,255,0) 17%), linear-gradient(234deg, rgba(233,48,201,.22) 0%, rgba(233,48,201,.13) 24%, rgba(255,255,255,0) 25%), linear-gradient(222deg, rgba(82,35,177,.20) 0%, rgba(82,35,177,.09) 34%, rgba(255,255,255,0) 35%), radial-gradient(circle at 100% 0%, rgba(233,48,201,.18) 0%, rgba(255,255,255,0) 52%)",
          }}
        />
        <div
          className="absolute inset-x-0 top-0 h-[430px]"
          style={{
            background:
              "radial-gradient(ellipse at 50% 12%, rgba(233,48,201,.16) 0%, rgba(120,35,177,.11) 24%, rgba(255,255,255,0) 58%), radial-gradient(ellipse at 50% 25%, rgba(255,255,255,.98) 0%, rgba(255,255,255,.92) 36%, rgba(255,255,255,.78) 57%, rgba(255,255,255,.20) 76%, rgba(255,255,255,0) 100%), linear-gradient(180deg, rgba(255,255,255,.12) 0%, rgba(255,255,255,.90) 76%, rgba(255,255,255,1) 100%)",
          }}
        />
      </div>
      <div className="relative z-10 mx-auto flex max-w-[1120px] flex-col items-center text-center">
        <div className="mb-7 inline-flex rounded-full bg-gradient-to-r from-[#e930c9] via-[#7823b1] to-[#2e1698] p-px">
          <div className="rounded-full bg-white px-4 py-1.5 text-[13px] font-semibold tracking-[0.02em] text-[#100f12]">
            {t(lang, "hero.badge")}
          </div>
        </div>
        <h1 className="mb-2 text-[40px] font-extrabold leading-[1.06] tracking-[-0.03em] sm:text-[58px] md:text-[76px]">
          {t(lang, "hero.title1")}
        </h1>
        <div className="relative mb-7 h-[1.08em] w-full overflow-hidden text-[40px] font-extrabold leading-[1.06] tracking-[-0.03em] text-[#5223b1] sm:text-[58px] md:text-[76px]">
          <motion.div
            className="absolute inset-x-0 top-0 flex flex-col items-center gap-2 pb-2"
            style={{ translateY }}
          >
            {[...rotateWords, rotateWords[0]].map((word, index) => {
              const activeIndex = index === rotateWords.length ? 0 : index;

              return (
                <motion.div
                  key={`${lang}-${word}-${index}`}
                  className="h-[1.08em]"
                  initial={false}
                  animate={{
                    opacity: wordIndex === activeIndex ? 1 : 0.45,
                    scale: wordIndex === activeIndex ? 1 : 0.98,
                  }}
                  transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    backgroundImage: "linear-gradient(90deg, #5223b1 0%, #7823b1 52%, #e930c9 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                    transformOrigin: "center center",
                  }}
                >
                  {word}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
        <p className="mb-9 max-w-[780px] text-lg leading-relaxed text-[#3a383f]">
          <HeroSubcopy lang={lang} />
        </p>
        <motion.div className="mb-20 flex flex-wrap justify-center gap-4" style={{ opacity: ctaOpacity }}>
          <DownloadLink
            to={downloadPath(lang, "hero_primary_cta", undefined, search)}
            onClick={() => reportLeadEntryConversion(search)}
            className="inline-block rounded-[10px] px-[34px] py-[18px] text-base font-semibold text-white no-underline"
            style={{
              background: "linear-gradient(265deg, #e930c9 0%, #7823b1 34%, #5223b1 64%, #2e1698 100%)",
              boxShadow: "0 8px 30px rgba(120,35,177,.35)",
            }}
          >
            {t(lang, "hero.cta.primary")}
          </DownloadLink>
          <a
            href="#how"
            className="inline-block rounded-[10px] border border-[#d3d3d3] bg-white px-[34px] py-[18px] text-base font-semibold text-[#100f12] no-underline transition-colors hover:border-[#a48cea]"
          >
            {t(lang, "hero.cta.secondary")}
          </a>
        </motion.div>

        <motion.div className="relative z-10 w-full max-w-[720px] text-left" style={{ opacity: demoOpacity }}>
          <form
            className="flex flex-col gap-3 rounded-2xl border border-[#e7dce7] bg-white p-2 shadow-[0_18px_60px_rgba(46,22,152,.08)] sm:h-[68px] sm:flex-row sm:items-center"
            onSubmit={submitDemoEmail}
          >
            <label htmlFor="clawease-demo-email" className="sr-only">
              {t(lang, "hero.demo.email")}
            </label>
            <input
              id="clawease-demo-email"
              type="email"
              value={demoEmail}
              onChange={(event) => setDemoEmail(event.target.value)}
              className="min-h-[48px] flex-1 rounded-xl border-0 bg-transparent px-5 text-base font-semibold text-[#100f12] outline-none placeholder:text-[#7a6079]"
              placeholder={t(lang, "hero.demo.email")}
            />
            <button
              type="submit"
              className="min-h-[48px] rounded-xl bg-[#281629] px-7 text-sm font-extrabold text-white shadow-[0_8px_22px_rgba(40,22,41,.18)] transition-transform hover:-translate-y-0.5"
            >
              {t(lang, "hero.demo.button")}
            </button>
          </form>
          <div className="relative left-1/2 mt-7 w-[120%] max-w-[864px] -translate-x-1/2 overflow-hidden whitespace-nowrap py-1">
            <motion.div
              className="flex w-max items-center gap-10 text-sm font-semibold text-[#775f78]"
              animate={{ x: ["0%", "-25%"] }}
              transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
            >
              {[...inputItems, ...inputItems, ...inputItems, ...inputItems].map((item, index) => {
                const Icon = item.icon;

                return (
                  <span key={`${item.key}-${index}`} className="inline-flex items-center gap-2.5">
                    <Icon size={15} strokeWidth={2.1} className="text-[#7b2fd0]" />
                    {t(lang, item.key)}
                  </span>
                );
              })}
            </motion.div>
          </div>
        </motion.div>
      </div>
      <div className="h-28" />
    </section>
  );
}
